// ─────────────────────────────────────────────────────────────────────────────
// KLOTHIQ — QuickAddCustomerModal
// ─────────────────────────────────────────────────────────────────────────────
// Lightweight inline modal for creating a new customer directly from the
// Sale Form without navigating away. On success, returns the new customer
// so it can be immediately selected in the ComboSelect.
// ─────────────────────────────────────────────────────────────────────────────

import UserGuide from '@/components/help/UserGuide';
import { Input } from '@/components/ui/FormControls';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { optionalCustomerPhoneSchema } from '@klothiq/validation';
import { X, UserPlus, Loader2 } from 'lucide-react';
import { useCreateCustomer } from '@/hooks/useCustomers';
import { toast } from 'sonner';
import type { Database } from '@klothiq/domain';

type Customer = Database['public']['Tables']['customers']['Row'];

// ── Validation Schema ─────────────────────────────────────────────────────────

const schema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100, 'Name too long'),
  phone: optionalCustomerPhoneSchema,
  address: z.string().max(200, 'Address too long').optional(),
  city: z.string().max(100, 'City too long').optional(),
});

type FormValues = z.infer<typeof schema>;

// ── Props ─────────────────────────────────────────────────────────────────────

export interface QuickAddCustomerModalProps {
  /** Pre-fill name from what the user typed in the ComboSelect */
  initialName?: string;
  onClose: () => void;
  /** Called with the newly created customer on success */
  onSuccess: (customer: Customer) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function QuickAddCustomerModal({
  initialName = '',
  onClose,
  onSuccess,
}: QuickAddCustomerModalProps) {
  const createCustomer = useCreateCustomer();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: initialName, phone: '', address: '', city: '' },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      const newCustomer = await createCustomer.mutateAsync({
        name: values.name.trim(),
        phone: values.phone?.trim() || null,
        address: values.address?.trim() || null,
        city: values.city?.trim() || null,
        is_active: true,
      });
      toast.success(`Customer "${newCustomer.name}" created`);
      onSuccess(newCustomer as Customer);
      onClose();
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message.includes('customers_phone')
            ? 'A customer with this phone number already exists.'
            : err.message
          : 'Failed to create customer';
      toast.error(msg);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-field-guide-scope>
      <div className="w-full max-w-md rounded-lg border border-border bg-card shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            <div className="flex items-start justify-between gap-3 pr-8">
              <h2 className="text-base font-semibold text-foreground">Add New Customer</h2>
              <UserGuide compact />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-4">
          {/* Name */}
          <div>
            <label htmlFor="qac-name" className="block text-sm font-medium text-foreground mb-1.5">
              Full Name <span className="text-destructive">*</span>
            </label>
            <Input
              id="qac-name"
              type="text"
              autoFocus
              {...register('name')}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="e.g. Rahul Menon"
              helpLabel="Full Name *"
            />
            {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>}
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="qac-phone" className="block text-sm font-medium text-foreground mb-1.5">
              Phone <span className="text-xs text-muted-foreground">(optional)</span>
            </label>
            <Input
              id="qac-phone"
              type="tel"
              {...register('phone')}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="+91 98765 43210"
              helpLabel="Phone (optional)"
            />
            {errors.phone && (
              <p className="mt-1 text-xs text-destructive">{errors.phone.message}</p>
            )}
          </div>

          {/* Address (optional) */}
          <div>
            <label
              htmlFor="qac-address"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              Address <span className="text-xs text-muted-foreground">(optional)</span>
            </label>
            <Input
              id="qac-address"
              type="text"
              {...register('address')}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Street / Area"
              helpLabel="Address (optional)"
            />
          </div>

          {/* City (optional) */}
          <div>
            <label htmlFor="qac-city" className="block text-sm font-medium text-foreground mb-1.5">
              City <span className="text-xs text-muted-foreground">(optional)</span>
            </label>
            <Input
              id="qac-city"
              type="text"
              {...register('city')}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="e.g. Kannur"
              helpLabel="City (optional)"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-md border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <UserPlus className="h-3.5 w-3.5" />
                  Save Customer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
